import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiX, FiBriefcase, FiDollarSign, FiUsers, FiTrendingUp, FiCalendar, FiDownload, FiFilter } from 'react-icons/fi';
import axiosConfig from '../../../api/axiosConfig';
import * as XLSX from 'xlsx';

const CompanyRevenueDetailsModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [sirketler, setSirketler] = useState([]);
  const [filteredSirketler, setFilteredSirketler] = useState([]);
  const [filterType, setFilterType] = useState('daily');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('revenue_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showOnlyWithRevenue, setShowOnlyWithRevenue] = useState(true);
  
  // Stats
  const [totalStats, setTotalStats] = useState({
    totalCompanies: 0,
    companiesWithRevenue: 0,
    totalRevenue: 0,
    totalLessons: 0,
    totalStudents: 0,
    averageRevenue: 0
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      fetchCompanyRevenue();
    }
  }, [isOpen, filterType]);

  const getDateRangeParams = useCallback(() => {
    const today = new Date();
    let params = {};
    
    if (filterType === 'daily') {
      const todayStr = today.getFullYear() + '-' + 
             String(today.getMonth() + 1).padStart(2, '0') + '-' + 
             String(today.getDate()).padStart(2, '0');
      params = {
        start_date: todayStr,
        end_date: todayStr,
        date_field: 'created_at'
      };
    } else if (filterType === 'weekly') {
      const startOfWeek = new Date(today);
      const dayOfWeek = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      params = {
        start_date: startOfWeek.toISOString().split('T')[0],
        end_date: endOfWeek.toISOString().split('T')[0],
        date_field: 'created_at'
      };
    } else if (filterType === 'monthly') {
      params = {
        start_date: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
        end_date: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0],
        date_field: 'created_at'
      };
    } else if (filterType === 'yearly') {
      params = {
        start_date: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0],
        end_date: new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0],
        date_field: 'created_at'
      };
    } else {
      params = { date_field: 'created_at' };
    }
    
    return params;
  }, [filterType]);

  const fetchCompanyRevenue = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dateParams = getDateRangeParams();
      
      // Paralel veri çekme
      const [sirketRes, derslerRes, ogrenciRes] = await Promise.all([
        axiosConfig.get('/admin/sirkets', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axiosConfig.get('/admin/ogretmenders', {
          headers: { Authorization: `Bearer ${token}` },
          params: { ...dateParams, limit: 100000 }
        }),
        axiosConfig.get('/admin/ogrencis', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const sirketlerData = sirketRes.data || [];
      const dersler = Array.isArray(derslerRes.data) ? derslerRes.data : (derslerRes.data.data || []);
      const ogrenciler = Array.isArray(ogrenciRes.data) ? ogrenciRes.data : (ogrenciRes.data.data || []);

      // Şirket verilerini işle
      const processedSirketler = sirketlerData.map(sirket => {
        // Şirkete kayıtlı öğrenciler
        const sirketOgrencileri = ogrenciler.filter(o => 
          String(o.registered_system) === String(sirket.id)
        );
        
        const sirketOgrenciIds = sirketOgrencileri.map(o => o.id);
        
        // Bu öğrencilerin derslerini filtrele
        const sirketDersleri = dersler.filter(ders => 
          sirketOgrenciIds.includes(ders.ogrenci_id)
        );
        
        // Şirket payı toplamı
        const toplamSirketPayi = sirketDersleri.reduce((sum, ders) => 
          sum + (parseFloat(ders.sirket_payi) || 0), 0
        );
        
        // Detaylı ders bilgileri
        const dersDetaylari = sirketDersleri.map(ders => ({
          id: ders.id,
          tarih: ders.baslangic_tarihi,
          ogrenciAdi: ders.ogrenci_name || ders.ogrenci_adi || '-',
          ogretmenAdi: ders.ogretmen_name || ders.ogretmen_adi || '-',
          konu: ders.ders_konusu || '-',
          sure: ders.ders_sure,
          sirketPayi: parseFloat(ders.sirket_payi) || 0
        }));
        
        return {
          id: sirket.id,
          sirketAdi: sirket.sirket_adi,
          ogrenciSayisi: sirketOgrencileri.length,
          aktifOgrenci: sirketOgrencileri.filter(o => o.status === 1).length,
          pasifOgrenci: sirketOgrencileri.filter(o => o.status === 0).length,
          dersSayisi: sirketDersleri.length,
          toplamKazanc: toplamSirketPayi,
          dersDetaylari: dersDetaylari.sort((a, b) => new Date(b.tarih) - new Date(a.tarih))
        };
      });

      setSirketler(processedSirketler);
      
      // İstatistikleri hesapla
      const stats = processedSirketler.reduce((acc, sirket) => {
        acc.totalCompanies++;
        if (sirket.toplamKazanc > 0) acc.companiesWithRevenue++;
        acc.totalRevenue += sirket.toplamKazanc;
        acc.totalLessons += sirket.dersSayisi;
        acc.totalStudents += sirket.ogrenciSayisi;
        return acc;
      }, {
        totalCompanies: 0,
        companiesWithRevenue: 0,
        totalRevenue: 0,
        totalLessons: 0,
        totalStudents: 0
      });
      
      stats.averageRevenue = stats.totalCompanies > 0 
        ? stats.totalRevenue / stats.totalCompanies 
        : 0;
      
      setTotalStats(stats);
      applyFiltersAndSort(processedSirketler);
    } catch (error) {
      console.error('Şirket verileri alınamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = useCallback((data = sirketler) => {
    let filtered = [...data];
    
    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.sirketAdi.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(s.id).includes(searchTerm)
      );
    }
    
    // Sadece kazancı olanları göster
    if (showOnlyWithRevenue) {
      filtered = filtered.filter(s => s.toplamKazanc > 0);
    }
    
    // Sıralama
    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'revenue_desc':
          return b.toplamKazanc - a.toplamKazanc;
        case 'revenue_asc':
          return a.toplamKazanc - b.toplamKazanc;
        case 'name_asc':
          return a.sirketAdi.localeCompare(b.sirketAdi);
        case 'name_desc':
          return b.sirketAdi.localeCompare(a.sirketAdi);
        case 'lessons_desc':
          return b.dersSayisi - a.dersSayisi;
        case 'lessons_asc':
          return a.dersSayisi - b.dersSayisi;
        default:
          return 0;
      }
    });
    
    setFilteredSirketler(filtered);
  }, [sirketler, searchTerm, showOnlyWithRevenue, sortBy]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [searchTerm, showOnlyWithRevenue, sortBy, sirketler]);

  // Sayfalanmış veriler
  const paginatedSirketler = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredSirketler.slice(startIndex, endIndex);
  }, [filteredSirketler, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSirketler.length / itemsPerPage);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getDateRangeText = () => {
    const today = new Date();
    if (filterType === 'daily') {
      return today.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } else if (filterType === 'weekly') {
      const startOfWeek = new Date(today);
      const dayOfWeek = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (filterType === 'monthly') {
      return today.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    } else if (filterType === 'yearly') {
      return today.getFullYear() + ' Yılı';
    } else {
      return 'Tüm Zamanlar';
    }
  };

  const exportToExcel = () => {
    const dataToExport = [];
    
    filteredSirketler.forEach(sirket => {
      sirket.dersDetaylari.forEach(ders => {
        dataToExport.push({
          'Şirket ID': sirket.id,
          'Şirket Adı': sirket.sirketAdi,
          'Öğrenci Sayısı': sirket.ogrenciSayisi,
          'Ders Tarihi': formatDate(ders.tarih),
          'Öğrenci': ders.ogrenciAdi,
          'Öğretmen': ders.ogretmenAdi,
          'Ders Konusu': ders.konu,
          'Ders Süresi': `${ders.sure} dk`,
          'Şirket Payı': ders.sirketPayi
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Şirket Kazançları");
    
    const fileName = `sirket_kazanclari_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const [expandedCompany, setExpandedCompany] = useState(null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <FiBriefcase className="mr-2 text-orange-600" />
              Şirket Kazanç Detayları
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {getDateRangeText()} - {totalStats.companiesWithRevenue} şirketin kazancı var
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <FiDownload className="text-lg" />
              Excel
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <FiX className="text-2xl text-gray-600" />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setFilterType('daily')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterType === 'daily'
                ? 'bg-orange-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Günlük
          </button>
          <button
            onClick={() => setFilterType('weekly')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterType === 'weekly'
                ? 'bg-orange-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Haftalık
          </button>
          <button
            onClick={() => setFilterType('monthly')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterType === 'monthly'
                ? 'bg-orange-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Aylık
          </button>
          <button
            onClick={() => setFilterType('yearly')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterType === 'yearly'
                ? 'bg-orange-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Yıllık
          </button>
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filterType === 'all'
                ? 'bg-orange-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Tüm Zamanlar
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Toplam Şirket</p>
              <p className="text-lg font-bold text-gray-800">{totalStats.totalCompanies}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Kazançlı Şirket</p>
              <p className="text-lg font-bold text-green-600">{totalStats.companiesWithRevenue}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Toplam Kazanç</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(totalStats.totalRevenue)}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Ortalama</p>
              <p className="text-lg font-bold text-purple-600">{formatCurrency(totalStats.averageRevenue)}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Ders Sayısı</p>
              <p className="text-lg font-bold text-indigo-600">{totalStats.totalLessons}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Öğrenci Sayısı</p>
              <p className="text-lg font-bold text-teal-600">{totalStats.totalStudents}</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Şirket ara..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="revenue_desc">En Yüksek Kazanç</option>
              <option value="revenue_asc">En Düşük Kazanç</option>
              <option value="lessons_desc">En Çok Ders</option>
              <option value="lessons_asc">En Az Ders</option>
              <option value="name_asc">A-Z Şirket</option>
              <option value="name_desc">Z-A Şirket</option>
            </select>
            <button
              onClick={() => setShowOnlyWithRevenue(!showOnlyWithRevenue)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showOnlyWithRevenue
                  ? 'bg-orange-100 text-orange-700 border border-orange-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300'
              }`}
            >
              <FiFilter className="w-4 h-4" />
              Sadece Kazançlı
            </button>
          </div>
        </div>

        {/* Pagination Top */}
        {filteredSirketler.length > itemsPerPage && (
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Toplam <span className="font-semibold">{filteredSirketler.length}</span> şirket
              <span className="ml-2">(
                {((currentPage - 1) * itemsPerPage) + 1} - 
                {Math.min(currentPage * itemsPerPage, filteredSirketler.length)} arası gösteriliyor
              )</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Önceki
              </button>
              <span className="px-3 py-1 text-sm">
                Sayfa {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sonraki →
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
          ) : paginatedSirketler.length === 0 ? (
            <div className="text-center py-12">
              <FiBriefcase className="text-5xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Bu döneme ait kazanç kaydı bulunamadı.</p>
            </div>
          ) : (
            <div className="space-y-4 p-4">
              {paginatedSirketler.map((sirket) => (
                <div key={sirket.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{sirket.sirketAdi}</h3>
                        <div className="flex gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center">
                            <FiUsers className="mr-1" />
                            {sirket.ogrenciSayisi} öğrenci ({sirket.aktifOgrenci} aktif)
                          </span>
                          <span className="flex items-center">
                            <FiCalendar className="mr-1" />
                            {sirket.dersSayisi} ders
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-orange-600">
                          {formatCurrency(sirket.toplamKazanc)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Ort: {formatCurrency(sirket.dersSayisi > 0 ? sirket.toplamKazanc / sirket.dersSayisi : 0)}/ders
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ders Detayları */}
                  {sirket.dersSayisi > 0 && (
                    <div className="p-4">
                      <details className="group">
                        <summary className="cursor-pointer text-sm text-gray-600 hover:text-orange-600 transition-colors">
                          Ders detaylarını göster ({sirket.dersSayisi} ders)
                        </summary>
                        <div className="mt-3">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="text-xs text-gray-500 border-b">
                                <tr>
                                  <th className="text-left py-2">Tarih</th>
                                  <th className="text-left py-2">Öğrenci</th>
                                  <th className="text-left py-2">Öğretmen</th>
                                  <th className="text-left py-2">Konu</th>
                                  <th className="text-center py-2">Süre</th>
                                  <th className="text-right py-2">Şirket Payı</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {sirket.dersDetaylari.slice(0, 5).map((ders, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="py-2 text-gray-600">{formatDate(ders.tarih)}</td>
                                    <td className="py-2 text-gray-700">{ders.ogrenciAdi}</td>
                                    <td className="py-2 text-gray-700">{ders.ogretmenAdi}</td>
                                    <td className="py-2 text-gray-600">{ders.konu}</td>
                                    <td className="py-2 text-center text-gray-600">{ders.sure} dk</td>
                                    <td className="py-2 text-right font-medium text-orange-600">
                                      {formatCurrency(ders.sirketPayi)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {sirket.dersSayisi > 5 && (
                              <p className="text-xs text-gray-500 text-center mt-2">
                                ... ve {sirket.dersSayisi - 5} ders daha
                              </p>
                            )}
                          </div>
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {filteredSirketler.length > itemsPerPage ? (
              <span>
                Sayfa {currentPage} / {totalPages} - 
                Toplam <span className="font-semibold">{filteredSirketler.length}</span> şirket
              </span>
            ) : (
              <span>Toplam <span className="font-semibold">{filteredSirketler.length}</span> şirket</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyRevenueDetailsModal;
